import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useCreate from "../../hooks/useCreate";
import useGet from "../../hooks/useGet";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useDelete from "../../hooks/useDelete";
import useUpdate from "../../hooks/useUpdate";
import API_BASE_URL from "../../config/api";
import { useAuth } from "pages/AuthProvider";

const emptyAttribute = { heading: "", valuesText: "" };

function CategorySubcategories() {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const { categoryId } = useParams();
  const { create } = useCreate("/api/categories");
  const { data: categoriesData } = useGet("/api/categories?all=true");
  const { data: subcategoriesData, loading, mutate } = useGet(
    categoryId ? `/api/categories/subcategories/${categoryId}?all=true` : null
  );

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    metaTitle: "",
    metaDescription: "",
    isActive: true,
    attributes: [{ ...emptyAttribute }]
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [bannerDesktopImage, setBannerDesktopImage] = useState(null);
  const [bannerDesktopPreview, setBannerDesktopPreview] = useState("");
  const [bannerMobileImage, setBannerMobileImage] = useState(null);
  const [bannerMobilePreview, setBannerMobilePreview] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewSubcategory, setViewSubcategory] = useState(null);

  const { remove } = useDelete("/api/categories");
  const { update } = useUpdate("/api/categories");

  const categories = useMemo(() => {
    if (Array.isArray(categoriesData)) return categoriesData;
    if (Array.isArray(categoriesData?.categories)) return categoriesData.categories;
    if (Array.isArray(categoriesData?.data)) return categoriesData.data;
    return [];
  }, [categoriesData]);

  const selectedCategory = useMemo(
    () => categories.find((cat) => String(cat._id || cat.id) === String(categoryId)),
    [categories, categoryId]
  );

  const subcategories = useMemo(() => {
    if (Array.isArray(subcategoriesData)) return subcategoriesData;
    if (Array.isArray(subcategoriesData?.subCategories)) return subcategoriesData.subCategories;
    if (Array.isArray(subcategoriesData?.subcategories)) return subcategoriesData.subcategories;
    if (Array.isArray(subcategoriesData?.data)) return subcategoriesData.data;
    return [];
  }, [subcategoriesData]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === "isActive" ? value === "true" : value }));
  };

  const handleAttributeChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.attributes];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, attributes: next };
    });
  };

  const addAttributeRow = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { ...emptyAttribute }]
    }));
  };

  const removeAttributeRow = (index) => {
    setFormData((prev) => {
      const next = prev.attributes.filter((_, i) => i !== index);
      return { ...prev, attributes: next.length ? next : [{ ...emptyAttribute }] };
    });
  };

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      if (bannerDesktopPreview && bannerDesktopPreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerDesktopPreview);
      }
      if (bannerMobilePreview && bannerMobilePreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerMobilePreview);
      }
    };
  }, [imagePreview, bannerDesktopPreview, bannerMobilePreview]);

  const clearImagePreview = () => {
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return "";
    });
  };

  const clearBannerDesktopPreview = () => {
    setBannerDesktopPreview((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return "";
    });
  };

  const clearBannerMobilePreview = () => {
    setBannerMobilePreview((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return "";
    });
  };

  const resolveImageUrl = (sub) => {
    const raw =
      sub?.image?.url ||
      sub?.image?.path ||
      sub?.imageUrl ||
      sub?.thumbnail ||
      sub?.photo ||
      sub?.image;

    if (!raw || typeof raw !== "string") return "";
    if (/^(https?:|blob:|data:)/i.test(raw)) return raw;

    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    return `${API_BASE_URL}/uploads/${normalized}`;
  };

  const resolveBannerDesktopUrl = (sub) => {
    const raw =
      sub?.bannerImageDesktop?.url ||
      sub?.bannerImageDesktop?.path ||
      sub?.bannerImageDesktopUrl ||
      sub?.bannerImageDesktop;

    if (!raw || typeof raw !== "string") return "";
    if (/^(https?:|blob:|data:)/i.test(raw)) return raw;

    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    return `${API_BASE_URL}/uploads/${normalized}`;
  };

  const resolveBannerMobileUrl = (sub) => {
    const raw =
      sub?.bannerImageMobile?.url ||
      sub?.bannerImageMobile?.path ||
      sub?.bannerImageMobileUrl ||
      sub?.bannerImageMobile;

    if (!raw || typeof raw !== "string") return "";
    if (/^(https?:|blob:|data:)/i.test(raw)) return raw;

    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    return `${API_BASE_URL}/uploads/${normalized}`;
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setImage(file);
    if (!file) return;
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
  };

  const handleBannerDesktopChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBannerDesktopImage(file);
    if (!file) return;
    setBannerDesktopPreview((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
  };

  const handleBannerMobileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBannerMobileImage(file);
    if (!file) return;
    setBannerMobilePreview((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      metaTitle: "",
      metaDescription: "",
      isActive: true,
      attributes: [{ ...emptyAttribute }]
    });
    setImage(null);
    clearImagePreview();
    setBannerDesktopImage(null);
    clearBannerDesktopPreview();
    setBannerMobileImage(null);
    clearBannerMobilePreview();
  };

  const getPreparedAttributes = () =>
    formData.attributes
      .map((attr) => ({
        heading: attr.heading.trim(),
        values: attr.valuesText
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
      }))
      .filter((attr) => attr.heading && attr.values.length);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("description", formData.description || "");
      payload.append("metaTitle", formData.metaTitle || "");
      payload.append("metaDescription", formData.metaDescription || "");
      payload.append("isActive", formData.isActive);
      payload.append("parentId", categoryId);

      const preparedAttributes = getPreparedAttributes();
      if (preparedAttributes.length) {
        payload.append("attributes", JSON.stringify(preparedAttributes));
      }
      if (image) payload.append("image", image);
      if (bannerDesktopImage) payload.append("bannerImageDesktop", bannerDesktopImage);
      if (bannerMobileImage) payload.append("bannerImageMobile", bannerMobileImage);

      if (editMode && editingId) {
        await update(editingId, payload);
        setMessage({ type: "success", text: "Subcategory updated successfully." });
      } else {
        await create(payload);
        setMessage({ type: "success", text: "Subcategory added successfully." });
      }

      await mutate();
      setOpen(false);
      resetForm();
      setEditMode(false);
      setEditingId(null);
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to add subcategory.";
      setMessage({ type: "error", text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (sub) => {
    setFormData({
      name: sub.name || "",
      description: sub.description || "",
      metaTitle: sub.metaTitle || "",
      metaDescription: sub.metaDescription || "",
      isActive: !!sub.isActive,
      attributes:
        Array.isArray(sub.attributes) && sub.attributes.length
          ? sub.attributes.map((a) => ({ heading: a.heading || "", valuesText: (a.values || []).join(",") }))
          : [{ ...emptyAttribute }]
    });
    setImage(null);
    setImagePreview(resolveImageUrl(sub));
    setBannerDesktopImage(null);
    setBannerDesktopPreview(resolveBannerDesktopUrl(sub));
    setBannerMobileImage(null);
    setBannerMobilePreview(resolveBannerMobileUrl(sub));
    setEditMode(true);
    setEditingId(sub._id || sub.id);
    setOpen(true);
  };

  const handleDelete = async (sub) => {
    const id = sub._id || sub.id;
    if (!id) return;
    const ok = window.confirm(`Delete subcategory "${sub.name || id}"?`);
    if (!ok) return;
    try {
      await remove(id);
      await mutate();
      setMessage({ type: "success", text: "Subcategory deleted." });
    } catch (err) {
      setMessage({ type: "error", text: err?.message || "Failed to delete subcategory." });
    }
  };

  const openViewModal = (sub) => {
    setViewSubcategory(sub);
    setIsViewOpen(true);
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          background: "linear-gradient(135deg, #fff7ed 0%, #fefce8 100%)"
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Subcategory Manager
            </Typography>
            <Typography color="text.secondary">Category: {selectedCategory?.name || "Unknown"}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate("/AddCategory")}>
              Back to Categories
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              Add Subcategory
            </Button>
          </Box>
        </Box>
      </Paper>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          {message.text ? (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          ) : null}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Attributes</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>Loading subcategories...</TableCell>
                  </TableRow>
                ) : subcategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>No subcategories found for this category.</TableCell>
                  </TableRow>
                ) : (
                  subcategories.map((sub, index) => (
                    <TableRow key={sub._id || sub.id || `${sub.name}-${index}`} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{sub.name || "-"}</TableCell>
                      <TableCell>
                        <Chip label={sub.isActive ? "Active" : "Inactive"} size="small" color={sub.isActive ? "success" : "default"} />
                      </TableCell>
                      <TableCell>{Array.isArray(sub.attributes) ? sub.attributes.length : 0}</TableCell>
                      <TableCell sx={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {sub.description || "-"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View">
                          <IconButton size="small" color="info" onClick={() => openViewModal(sub)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canAccess('categories', 'edit') && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEdit(sub)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAccess('categories', 'delete') && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(sub)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editMode ? "Edit Subcategory" : "Create Subcategory"}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="subcategory-form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Subcategory Name" name="name" value={formData.name} onChange={handleBasicChange} required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleBasicChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Meta Title"
                  name="metaTitle"
                  value={formData.metaTitle}
                  onChange={handleBasicChange}
                  inputProps={{ maxLength: 60 }}
                  helperText={`${formData.metaTitle.length}/60`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Meta Description"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleBasicChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  name="isActive"
                  value={String(formData.isActive)}
                  onChange={handleBasicChange}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth sx={{ height: "56px" }}>
                  {editMode ? "Upload/Replace Image" : "Upload Image"}
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth sx={{ height: "56px" }}>
                  {editMode ? "Upload/Replace Banner (Desktop)" : "Upload Banner (Desktop)"}
                  <input type="file" hidden accept="image/*" onChange={handleBannerDesktopChange} />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth sx={{ height: "56px" }}>
                  {editMode ? "Upload/Replace Banner (Mobile)" : "Upload Banner (Mobile)"}
                  <input type="file" hidden accept="image/*" onChange={handleBannerMobileChange} />
                </Button>
              </Grid>
            </Grid>

            {imagePreview ? (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <img src={imagePreview} alt="subcategory preview" style={{ width: 76, height: 76, borderRadius: 10, objectFit: "cover" }} />
                <Typography variant="caption" color="text.secondary">
                  {image ? `Selected image: ${image.name}` : "Current image"}
                </Typography>
              </Box>
            ) : null}

            {bannerDesktopPreview ? (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <img
                  src={bannerDesktopPreview}
                  alt="subcategory banner desktop preview"
                  style={{ width: 160, height: 76, borderRadius: 10, objectFit: "cover" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {bannerDesktopImage ? `Selected desktop banner: ${bannerDesktopImage.name}` : "Current desktop banner"}
                </Typography>
              </Box>
            ) : null}

            {bannerMobilePreview ? (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <img
                  src={bannerMobilePreview}
                  alt="subcategory banner mobile preview"
                  style={{ width: 120, height: 76, borderRadius: 10, objectFit: "cover" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {bannerMobileImage ? `Selected mobile banner: ${bannerMobileImage.name}` : "Current mobile banner"}
                </Typography>
              </Box>
            ) : null}

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Attributes
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton color="primary" onClick={addAttributeRow}>
                  <AddCircleOutlineIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => removeAttributeRow(formData.attributes.length - 1)}
                  disabled={formData.attributes.length === 1}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gap: 1.5 }}>
              {formData.attributes.map((attr, index) => (
                <Grid container spacing={1.5} key={`sub-attr-${index}`}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={`Heading ${index + 1}`}
                      value={attr.heading}
                      onChange={(e) => handleAttributeChange(index, "heading", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label={`Values ${index + 1}`}
                      placeholder="Comma separated values"
                      value={attr.valuesText}
                      onChange={(e) => handleAttributeChange(index, "valuesText", e.target.value)}
                    />
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form="subcategory-form" variant="contained" disabled={submitting}>
            {submitting ? "Saving..." : editMode ? "Update Subcategory" : "Save Subcategory"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isViewOpen} onClose={() => setIsViewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Subcategory Details</DialogTitle>
        <DialogContent dividers>
          {viewSubcategory ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  p: 2,
                  background: "linear-gradient(135deg, #fff7ed 0%, #fefce8 100%)"
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Box
                      sx={{
                        width: { xs: "100%", md: 140 },
                        height: 140,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #cbd5e1",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveImageUrl(viewSubcategory) ? (
                        <img
                          src={resolveImageUrl(viewSubcategory)}
                          alt={viewSubcategory.name || "Subcategory"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No image
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {viewSubcategory.name || "-"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                      <Chip size="small" label={viewSubcategory.isActive ? "Active" : "Inactive"} color={viewSubcategory.isActive ? "success" : "default"} />
                      <Chip size="small" variant="outlined" label={`Category: ${selectedCategory?.name || "-"}`} />
                      <Chip size="small" variant="outlined" label={`Attributes: ${Array.isArray(viewSubcategory.attributes) ? viewSubcategory.attributes.length : 0}`} />
                    </Box>
                    <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                      {viewSubcategory.description || "No description available."}
                    </Typography>
                    {viewSubcategory.metaTitle ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Meta Title: {viewSubcategory.metaTitle}
                      </Typography>
                    ) : null}
                    {viewSubcategory.metaDescription ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Meta Description: {viewSubcategory.metaDescription}
                      </Typography>
                    ) : null}
                  </Grid>
                </Grid>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Banner Images
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Desktop
                    </Typography>
                    <Box
                      sx={{
                        width: 260,
                        height: 160,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveBannerDesktopUrl(viewSubcategory) ? (
                        <img
                          src={resolveBannerDesktopUrl(viewSubcategory)}
                          alt={`${viewSubcategory.name || "Subcategory"} banner desktop`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No desktop banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Mobile
                    </Typography>
                    <Box
                      sx={{
                        width: 180,
                        height: 160,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveBannerMobileUrl(viewSubcategory) ? (
                        <img
                          src={resolveBannerMobileUrl(viewSubcategory)}
                          alt={`${viewSubcategory.name || "Subcategory"} banner mobile`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No mobile banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Attributes
                </Typography>
                {Array.isArray(viewSubcategory.attributes) && viewSubcategory.attributes.length ? (
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {viewSubcategory.attributes.map((attr, idx) => (
                      <Box key={`view-sub-attr-${idx}`} sx={{ display: "grid", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          {idx + 1}. {attr.heading || "-"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {Array.isArray(attr.values) && attr.values.length ? (
                            attr.values.map((value, valueIndex) => (
                              <Chip key={`view-sub-attr-${idx}-value-${valueIndex}`} size="small" label={value} />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No values
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No attributes configured.</Typography>
                )}
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategorySubcategories;
